/**
 * ADR-101 / AM-011 — Scrap Reason Codes & Scrap Records.
 *
 * See the hook-naming note at the top of MaterialConsumptionPage.tsx — the
 * same caveat applies here: regenerate v1.ts via orval after the backend
 * merges, then verify these import names against the actual output.
 */
import { useState } from "react";
import {
  useV1ProductionScrapReasonCodesList,
  useV1ProductionScrapReasonCodesCreate,
  useV1ProductionScrapReasonCodesDeactivateCreate,
  useV1ProductionScrapReasonCodesActivateCreate,
  useV1ProductionScrapRecordsList,
  useV1ProductionScrapRecordsCreate,
  useV1ProductionWorkOrdersList,
  useV1ProductionBomItemsList,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export default function ScrapTrackingPage() {
  const [tab, setTab] = useState<"records" | "reasons">("records");

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem", fontFamily: "var(--font-family-fa, sans-serif)" }} dir="rtl">
      <PageBanner
        title="ردیابی ضایعات"
        subtitle="ثبت ضایعات تولیدی و مدیریت کدهای علت خرابی در ایستگاه‌های کاری"
        icon="🗑️"
        helpLink="/help/production/scrap"
      />

      <div className="brand-card-extand" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        <div style={{ display: "flex", gap: "1rem", borderBottom: "1px solid var(--color-border, #E4DCC8)", paddingBottom: "0.5rem" }}>
          <TabButton active={tab === "records"} onClick={() => setTab("records")}>
            ثبت ضایعات
          </TabButton>
          <TabButton active={tab === "reasons"} onClick={() => setTab("reasons")}>
            کدهای علت ضایعات
          </TabButton>
        </div>

        {tab === "records" ? <ScrapRecordsPanel /> : <ScrapReasonCodesPanel />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "0.5rem 1rem",
        marginBottom: "-1px",
        borderBottom: `2px solid ${active ? "#007270" : "transparent"}`,
        color: active ? "#007270" : "#888",
        fontWeight: active ? 700 : 500,
        fontSize: "0.9rem",
        background: "none",
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        cursor: "pointer",
        transition: "all 0.2s"
      }}
    >
      {children}
    </button>
  );
}

function ScrapRecordsPanel() {
  const { data, isLoading, isError, refetch } = useV1ProductionScrapRecordsList();
  const { data: workOrders } = useV1ProductionWorkOrdersList();
  const { data: bomItems } = useV1ProductionBomItemsList();
  const { data: reasons } = useV1ProductionScrapReasonCodesList();

  const createMutation = useV1ProductionScrapRecordsCreate();

  const [form, setForm] = useState({
    workOrder: "",
    bomItem: "",
    reasonCode: "",
    quantity: "",
    notes: "",
  });
  const [formError, setFormError] = useState<string | null>(null);

  const activeReasons = (reasons?.results ?? []).filter((r: any) => r.is_active);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.workOrder || !form.reasonCode || !form.quantity) {
      setFormError("سفارش کار، کد علت و مقدار الزامی است.");
      return;
    }
    try {
      await createMutation.mutateAsync({
        data: {
          work_order: form.workOrder,
          bom_item: form.bomItem || undefined,
          reason_code: form.reasonCode,
          quantity: form.quantity,
          notes: form.notes,
        },
      });
      setForm({ workOrder: "", bomItem: "", reasonCode: "", quantity: "", notes: "" });
      refetch();
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || "خطا در ثبت ضایعات.");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end", padding: "1.5rem", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)", background: "rgba(255,255,255,0.5)" }}>
        <div style={{ flex: "1 1 200px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>سفارش کار</label>
          <select
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
            value={form.workOrder}
            onChange={(e) => setForm((f) => ({ ...f, workOrder: e.target.value }))}
          >
            <option value="">انتخاب کنید...</option>
            {workOrders?.results?.map((wo: any) => (
              <option key={wo.id} value={wo.id}>
                WO #{wo.sequence_no} — {wo.status}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>قطعه (اختیاری)</label>
          <select
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
            value={form.bomItem}
            onChange={(e) => setForm((f) => ({ ...f, bomItem: e.target.value }))}
          >
            <option value="">— ضایعات محصول نهایی —</option>
            {bomItems?.results?.map((item: any) => (
              <option key={item.id} value={item.id}>
                {item.material_revision_display?.name ?? item.id}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: "1 1 200px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد علت</label>
          <select
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
            value={form.reasonCode}
            onChange={(e) => setForm((f) => ({ ...f, reasonCode: e.target.value }))}
          >
            <option value="">انتخاب کنید...</option>
            {activeReasons.map((r: any) => (
              <option key={r.id} value={r.id}>
                {r.code} — {r.description}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>مقدار</label>
          <input
            type="number"
            step="0.0001"
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
          />
        </div>
        <div style={{ flex: "2 1 250px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>یادداشت</label>
          <input
            type="text"
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
        <div style={{ flex: "0 0 auto" }}>
          <button
            type="submit"
            disabled={createMutation.isPending}
            style={{ padding: "8px 16px", borderRadius: "10px", background: "#e11d48", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: createMutation.isPending ? "not-allowed" : "pointer", opacity: createMutation.isPending ? 0.7 : 1 }}
          >
            ثبت ضایعات
          </button>
        </div>
        {formError && <p style={{ fontSize: "0.85rem", color: "#e11d48", width: "100%", margin: 0 }}>{formError}</p>}
      </form>

      {isLoading && <p style={{ fontSize: "0.85rem", color: "#888" }}>در حال بارگذاری...</p>}
      {isError && <p style={{ fontSize: "0.85rem", color: "#e11d48" }}>خطا در دریافت رکوردهای ضایعات.</p>}

      {!isLoading && !isError && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--color-border, #E4DCC8)", color: "#888", textAlign: "right" }}>
                <th style={{ padding: "0.75rem" }}>سفارش کار</th>
                <th style={{ padding: "0.75rem" }}>قطعه</th>
                <th style={{ padding: "0.75rem" }}>علت</th>
                <th style={{ padding: "0.75rem" }}>مقدار</th>
                <th style={{ padding: "0.75rem" }}>تاریخ</th>
              </tr>
            </thead>
            <tbody>
              {data?.results?.length ? (
                data.results.map((row: any) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                    <td style={{ padding: "0.75rem" }}>{row.work_order}</td>
                    <td style={{ padding: "0.75rem" }}>{row.bom_item ?? "محصول نهایی"}</td>
                    <td style={{ padding: "0.75rem" }}>{row.reason_code_display}</td>
                    <td style={{ padding: "0.75rem" }}>{row.quantity}</td>
                    <td style={{ padding: "0.75rem" }} dir="ltr">
                      <span style={{ display: "inline-block", textAlign: "right", direction: "rtl" }}>
                        {new Date(row.recorded_at).toLocaleString("fa-IR")}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ padding: "1.5rem", textAlign: "center", color: "#888" }}>
                    ضایعاتی ثبت نشده است.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScrapReasonCodesPanel() {
  const { data, isLoading, isError, refetch } = useV1ProductionScrapReasonCodesList();
  const createMutation = useV1ProductionScrapReasonCodesCreate();
  const deactivateMutation = useV1ProductionScrapReasonCodesDeactivateCreate();
  const activateMutation = useV1ProductionScrapReasonCodesActivateCreate();

  const [form, setForm] = useState({ code: "", description: "" });
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!form.code.trim()) {
      setFormError("کد الزامی است.");
      return;
    }
    try {
      await createMutation.mutateAsync({ data: { code: form.code, description: form.description } });
      setForm({ code: "", description: "" });
      refetch();
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || "این کد قبلاً برای این سازمان ثبت شده است.");
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    if (isActive) {
      await deactivateMutation.mutateAsync({ id, data: {} as any });
    } else {
      await activateMutation.mutateAsync({ id, data: {} as any });
    }
    refetch();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end", padding: "1.5rem", borderRadius: "12px", border: "1px solid var(--color-border, #E4DCC8)", background: "rgba(255,255,255,0.5)" }}>
        <div style={{ flex: "1 1 150px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>کد</label>
          <input
            type="text"
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
          />
        </div>
        <div style={{ flex: "2 1 300px" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>توضیح</label>
          <input
            type="text"
            style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>
        <div style={{ flex: "0 0 auto" }}>
          <button
            type="submit"
            disabled={createMutation.isPending}
            style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: createMutation.isPending ? "not-allowed" : "pointer", opacity: createMutation.isPending ? 0.7 : 1 }}
          >
            افزودن
          </button>
        </div>
        {formError && <p style={{ fontSize: "0.85rem", color: "#e11d48", width: "100%", margin: 0 }}>{formError}</p>}
      </form>

      {isLoading && <p style={{ fontSize: "0.85rem", color: "#888" }}>در حال بارگذاری...</p>}
      {isError && <p style={{ fontSize: "0.85rem", color: "#e11d48" }}>خطا در دریافت کدهای علت.</p>}

      {!isLoading && !isError && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--color-border, #E4DCC8)", color: "#888", textAlign: "right" }}>
                <th style={{ padding: "0.75rem" }}>کد</th>
                <th style={{ padding: "0.75rem" }}>توضیح</th>
                <th style={{ padding: "0.75rem" }}>وضعیت</th>
                <th style={{ padding: "0.75rem" }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {data?.results?.length ? (
                data.results.map((row: any) => (
                  <tr key={row.id} style={{ borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                    <td style={{ padding: "0.75rem", fontWeight: 700, color: "#2B2B2B" }}>{row.code}</td>
                    <td style={{ padding: "0.75rem" }}>{row.description}</td>
                    <td style={{ padding: "0.75rem" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "8px",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          background: row.is_active ? "rgba(16, 185, 129, 0.1)" : "rgba(136, 136, 136, 0.1)",
                          color: row.is_active ? "#10B981" : "#888",
                        }}
                      >
                        {row.is_active ? "فعال" : "غیرفعال"}
                      </span>
                    </td>
                    <td style={{ padding: "0.75rem" }}>
                      <button
                        style={{ background: "none", border: "none", color: "#007270", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => toggleActive(row.id, row.is_active)}
                      >
                        {row.is_active ? "غیرفعال کردن" : "فعال کردن"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ padding: "1.5rem", textAlign: "center", color: "#888" }}>
                    کد علتی ثبت نشده است.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
